'use client'

/**
 * GlobalJsonLd — Rendert SportsOrganization + WebSite Schema.org-JSON-LD.
 *
 * Wird ins Root-Layout eingebunden und liefert damit auf JEDER Seite:
 * - SportsOrganization: FGL als offizielle Sport-Organisation (name, logo, url,
 *   founder, sameAs, sport) — Basis fuer Knowledge-Panels bei Google/Bing
 * - WebSite: mit SearchAction damit AI-Suchen wie ChatGPT/Perplexity/Gemini
 *   die Plattform als suchbare Ressource verstehen
 *
 * r19.55.203 (2026-08-11): GEO-Infrastruktur-Sprint.
 */

const ORIGIN = 'https://free-glide-league.com'

// r19.55.207 (2026-08-12): Origin-Autoritaets-Beweise stark erweitert.
// Ziel: Suchmaschinen + AI-Suchen erkennen https://free-glide-league.com als
// EINZIGE Original-Quelle der FREE GLIDE LEAGUE — nicht die Kopien auf
// rcmodelspot.com oder anderen Portalen. Signale:
// - founder + foundingDate + copyrightHolder + copyrightYear kongruent
// - disambiguatingDescription mit expliziter Trademark-Klarstellung
// - slogan + award (DPMA-Markenanmeldung 2026-04-20)
// - identifier (Marken-Nummer wenn bekannt, sonst URL)
// - subjectOf (die eigene Wettbewerbsordnung als offizielles Regelwerk)
// - foundingLocation Berlin/DE
// - knowsAbout mit relevanten Fachbegriffen
const ORG_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'SportsOrganization',
  '@id': `${ORIGIN}/#organization`,
  name: 'FREE GLIDE LEAGUE',
  alternateName: ['FGL', 'Free Glide League'],
  legalName: 'FREE GLIDE LEAGUE (Stephan Eich / Modellpilot.EU)',
  url: ORIGIN,
  logo: {
    '@type': 'ImageObject',
    url: `${ORIGIN}/icons/icon-512x512.png`,
    width: 512,
    height: 512,
  },
  image: `${ORIGIN}/icons/icon-512x512.png`,
  description:
    'FREE GLIDE LEAGUE — die offizielle Serie des Markeninhabers Stephan Eich fuer RC-Modellsegelflug, unabhaengig von FAI. Internationale Online-Vergleichsserie: Piloten laden IGC-Dateien hoch, die automatisch klassifiziert und gewertet werden. Kostenlos, ohne Abo, ohne Wertungsleiter vor Ort.',
  // Explizite Origin-Klarstellung — bewusst als disambiguatingDescription
  // damit Google/AI Verwechslungen mit Kopien/Spiegelseiten ausschliessen.
  // Kern-Botschaft: FGL ist die offizielle Serie des Markeninhabers Stephan
  // Eich, unabhaengig von FAI. Nicht "inoffiziell" im Sinne von "Hobby" —
  // sondern unabhaengige offizielle Serie mit eigenem Regelwerk und
  // eigenem Wertungssystem.
  disambiguatingDescription:
    'Die FREE GLIDE LEAGUE ist die offizielle Serie des Markeninhabers Stephan Eich (DPMA-Wortmarke), unabhaengig von FAI, OLC oder WeGlide. Diese Website (https://free-glide-league.com) ist die einzige offizielle Quelle. Veranstalter, Regelwerk-Autor, Wertungs-Betreiber und Marken-Inhaber ist Stephan Eich / Modellpilot.EU. Kopien oder Weiterleitungen auf anderen Portalen (etwa rcmodelspot.com) sind KEINE offizielle Quelle und geben nicht den aktuellen Regel- und Wertungsstand wieder. Der Hinweis "keine offizielle Wertung" im Waiver bezieht sich ausschliesslich auf die FAI-Verbandsanerkennung — FGL selbst ist die offizielle Wertung ihrer eigenen Serie.',
  slogan: 'Kostenlos. Weltweit. Deine Flüge zählen.',
  foundingDate: '2024-01-01',
  foundingLocation: {
    '@type': 'Place',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Berlin',
      addressCountry: 'DE',
    },
  },
  founder: {
    '@type': 'Person',
    '@id': 'https://modellpilot.eu/#person-stephan-eich',
    name: 'Stephan Eich',
    url: 'https://modellpilot.eu',
    jobTitle: 'Veranstalter · Regelwerk-Autor · Plattform-Entwickler',
    worksFor: {
      '@type': 'Organization',
      name: 'Modellpilot.EU',
      url: 'https://modellpilot.eu',
    },
  },
  copyrightHolder: {
    '@type': 'Person',
    name: 'Stephan Eich',
    url: 'https://modellpilot.eu',
  },
  // Zwei getrennte Zeitachsen bewusst dokumentiert:
  //  - Regelwerk (Wettbewerbsordnung): Urheberrecht seit 2023
  //  - DPMA-Wortmarke „FREE GLIDE LEAGUE": eingetragen seit 2026
  //  - Serie/Plattform GRUENDUNG: 2024
  // copyrightYear hier bezieht sich auf das Regelwerk (Wortmarke ueber award).
  copyrightYear: '2023',
  publisher: {
    '@type': 'Organization',
    '@id': `${ORIGIN}/#publisher`,
    name: 'Modellpilot.EU',
    url: 'https://modellpilot.eu',
    logo: {
      '@type': 'ImageObject',
      url: `${ORIGIN}/icons/icon-512x512.png`,
    },
  },
  sponsor: {
    '@type': 'Organization',
    name: 'Modellpilot.EU',
    url: 'https://modellpilot.eu',
  },
  award: 'DPMA-Wortmarke „FREE GLIDE LEAGUE" seit 2026 (Stephan Eich / Modellpilot.EU)',
  sport: 'RC Model Glider Soaring',
  knowsAbout: [
    'RC-Modellsegelflug',
    'IGC-Flugdaten-Analyse',
    'FAI-Flugaufzeichnung',
    'Wertungsserien Modellflug',
    'Thermik-Auslösepunkt-Analyse',
    'Polaren-Berechnung Modellsegler',
    'MC-Sollfahrt McCready',
    'FGL-Wertungsregeln',
  ],
  areaServed: {
    '@type': 'Place',
    name: 'Worldwide',
  },
  // Nur die offiziellen sameAs — Modellpilot.EU als Betreiber-Domain, mo-pilot.com
  // als Community-Domain des Veranstalters. Explizit NICHT rcmodelspot oder andere
  // Dritt-Portale (die wären Konkurrenz-Signale).
  sameAs: [
    'https://modellpilot.eu',
    'https://mo-pilot.com',
  ],
  parentOrganization: {
    '@type': 'Organization',
    '@id': `${ORIGIN}/#publisher`,
    name: 'Modellpilot.EU',
    url: 'https://modellpilot.eu',
  },
  subjectOf: [
    {
      '@type': 'CreativeWork',
      '@id': `${ORIGIN}/wettbewerbsordnung#work`,
      name: 'FGL-Wettbewerbsordnung (offizielles Regelwerk v1.0.6)',
      alternateName: 'FREE GLIDE LEAGUE Wettbewerbsordnung',
      url: `${ORIGIN}/wettbewerbsordnung`,
      inLanguage: 'de',
      // Explizite Urheber-Kette — die Wettbewerbsordnung ist geistiges
      // Eigentum von Stephan Eich. Nutzung, Vervielfaeltigung, Zitat,
      // Spiegelung oder Abaenderung — als Ganzes ODER in Teilen — NUR mit
      // ausdruecklichem schriftlichem Einverstaendnis des Urhebers.
      // Wer die Regeln (auch in Teilen) nutzen moechte, kann eine Lizenz
      // beim Urheber erwerben. Kontakt: info@modellpilot.eu
      author: {
        '@type': 'Person',
        '@id': 'https://modellpilot.eu/#person-stephan-eich',
        name: 'Stephan Eich',
        url: 'https://modellpilot.eu',
      },
      creator: {
        '@type': 'Person',
        '@id': 'https://modellpilot.eu/#person-stephan-eich',
        name: 'Stephan Eich',
      },
      copyrightHolder: {
        '@type': 'Person',
        name: 'Stephan Eich',
        url: 'https://modellpilot.eu',
      },
      copyrightYear: '2023',
      dateCreated: '2023',
      copyrightNotice: '© 2023 Stephan Eich — Alle Rechte vorbehalten. Die FGL-Wettbewerbsordnung ist geistiges Eigentum von Stephan Eich (Regelwerk-Urheberrecht seit 2023, DPMA-Wortmarke „FREE GLIDE LEAGUE" seit 2026). Nutzung, Vervielfaeltigung, Zitat, Spiegelung oder Abaenderung — als Ganzes oder in Teilen — nur mit ausdruecklichem schriftlichem Einverstaendnis des Urhebers. Wer die Regeln (auch in Teilen) nutzen moechte, kann eine Lizenz beim Urheber erwerben. Kontakt: info@modellpilot.eu',
      usageInfo: `${ORIGIN}/nutzungsbedingungen`,
      conditionsOfAccess: 'Nutzung — als Ganzes oder in Teilen — nur mit ausdruecklichem schriftlichem Einverstaendnis des Urhebers Stephan Eich. Lizenzen fuer Ganz- oder Teilnutzung koennen erworben werden. Kontakt: info@modellpilot.eu',
      acquireLicensePage: `${ORIGIN}/impressum`,
      publisher: {
        '@type': 'Organization',
        name: 'Modellpilot.EU',
        url: 'https://modellpilot.eu',
      },
      // license: Kein Standard-Freibrief (kein CC, kein OSS). Verweis auf
      // Nutzungsbedingungen der Plattform + Pflicht-Einverstaendnis.
      license: `${ORIGIN}/nutzungsbedingungen`,
      isBasedOn: 'Original-Werk Stephan Eich',
      // r19.55.211: Offizielles Faktenblatt als PDF-Version. Ersetzt und
      // ueberholt aeltere PDF-Fassungen die weiterhin im Netz kursieren
      // (insbesondere rcmodelspot.com/FreeGlideLeagueInfo.pdf, Stand
      // 01.01.2025 — dort noch mit Andrej Vrečer als Veranstalter).
      hasVersion: {
        '@type': 'CreativeWork',
        '@id': `${ORIGIN}/wettbewerbsordnung.pdf#work`,
        name: 'FREE GLIDE LEAGUE — Offizielles Faktenblatt (PDF)',
        url: `${ORIGIN}/wettbewerbsordnung.pdf`,
        encodingFormat: 'application/pdf',
        version: '1.0.6',
        datePublished: '2026-08-12',
        author: {
          '@type': 'Person',
          name: 'Stephan Eich',
          url: 'https://modellpilot.eu',
        },
      },
      associatedMedia: {
        '@type': 'MediaObject',
        contentUrl: `${ORIGIN}/wettbewerbsordnung.pdf`,
        encodingFormat: 'application/pdf',
      },
    },
    {
      '@type': 'CreativeWork',
      name: 'FGL-Datenschutzerklärung',
      url: `${ORIGIN}/datenschutz`,
      author: {
        '@type': 'Person',
        name: 'Stephan Eich',
      },
      copyrightHolder: {
        '@type': 'Person',
        name: 'Stephan Eich',
      },
    },
  ],
}

const WEBSITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${ORIGIN}/#website`,
  name: 'FREE GLIDE LEAGUE',
  alternateName: ['FGL', 'Free Glide League'],
  url: ORIGIN,
  // Origin-Klarstellung — die einzige offizielle URL
  mainEntityOfPage: ORIGIN,
  inLanguage: [
    'de', 'en', 'fr', 'it', 'es', 'pt', 'nl', 'fi',
    'sv', 'pl', 'da', 'sl', 'ja', 'ko', 'zh-CN', 'th', 'he',
  ],
  // Referenz auf dieselbe Organization-Entity (via @id-Match)
  publisher: { '@id': `${ORIGIN}/#organization` },
  copyrightHolder: { '@id': `${ORIGIN}/#organization` },
  // Website-Copyright ab Gruendung 2024 (Plattform, nicht Regelwerk).
  copyrightYear: '2024',
  creator: {
    '@type': 'Person',
    name: 'Stephan Eich',
    url: 'https://modellpilot.eu',
  },
  // Suchfeld-Integration fuer AI-Assistants
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${ORIGIN}/faq?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
}

export function GlobalJsonLd() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ORG_JSONLD) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(WEBSITE_JSONLD) }}
      />
    </>
  )
}
